<template>
  <div class="floating-node-panel" :class="{ 'collapsed': panelCollapsed }">
    <v-card elevation="8" class="panel-card">
      <v-card-title class="panel-header">
        <div class="header-content">
          <v-icon class="header-icon">mdi-view-grid-plus</v-icon>
          <span v-if="!panelCollapsed" class="panel-title">Nodes</span>
        </div>
      </v-card-title>

      <v-card-text v-if="!panelCollapsed" class="panel-content">
        <!-- Search -->
        <div class="search-container">
          <v-text-field v-model="searchTerm" placeholder="Search nodes..." density="compact" variant="outlined"
            hide-details prepend-inner-icon="mdi-magnify" clearable />
        </div>

        <!-- Node Categories -->
        <div class="categories">
          <v-expansion-panels v-model="openPanels" multiple variant="accordion" class="category-panels">
            <v-expansion-panel v-for="(category, index) in filteredCategories" :key="category.name" :value="index">
              <v-expansion-panel-title class="category-header">
                <div class="category-title-content">
                  <v-icon size="16" class="category-icon">{{ category.icon }}</v-icon>
                  <span class="category-title">{{ category.name }}</span>
                  <v-chip size="x-small" class="node-count">{{ category.nodes.length }}</v-chip>
                </div>
              </v-expansion-panel-title>

              <v-expansion-panel-text class="category-content">
                <div class="category-nodes">
                  <div v-for="node in category.nodes" :key="node.type" class="draggable-node"
                    :class="[`node-${node.type}`, { 'is-preview-node': previewNodeTypes.includes(node.type) }]"
                    draggable="true" @dragstart="onDragStart($event, node)"
                    @click="addNode(node)">
                    <span v-if="previewNodeTypes.includes(node.type)" class="preview-chip-panel"
                      title="This node is a preview/beta feature">PREVIEW</span>
                    <div class="node-preview">
                      <div class="node-icon" :style="{ backgroundColor: node.iconBg, color: node.iconColor }">
                        <v-icon size="16">{{ node.icon }}</v-icon>
                      </div>
                      <div class="node-info">
                        <div class="node-name">{{ node.name }}</div>
                        <div class="node-description">{{ node.description }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </v-card-text>

      <!-- Collapsed state -->
      <v-card-text v-else class="collapsed-content">
        <div class="quick-nodes">
          <v-tooltip v-for="node in quickNodes" :key="node.type" :text="node.name" location="right">
            <template v-slot:activator="{ props }">
              <div v-bind="props" class="quick-node" draggable="true" @dragstart="onDragStart($event, node)"
                @click="addNode(node)">
                <v-icon size="18" :color="node.iconColor">{{ node.icon }}</v-icon>
              </div>
            </template>
          </v-tooltip>
        </div>
      </v-card-text>
      <v-card-action style="display: flex; justify-content: flex-end; align-items: center;"
        :style="panelCollapsed ? 'justify-content: center;padding-bottom: 5px;' : 'justify-content: flex-end;'">
        <v-btn icon size="small"  @click="togglePanel" class="toggle-btn collapse-btn"
          :class="{ 'collapsed-hint animated-hint-btn': panelCollapsed }">
          <div class="btn-content">
            <!-- Hint animation elements -->
            <div v-if="panelCollapsed" class="hint-circle hint-circle-1"></div>
            <div v-if="panelCollapsed" class="hint-circle hint-circle-2"></div>
            <div v-if="panelCollapsed" class="hint-circle hint-circle-3"></div>

            <!-- Original content -->
            <div class="original-content">
              <transition name="collapse-text" mode="out-in">
                <span v-if="!panelCollapsed" class="collapse-text">Collapse</span>
              </transition>
              <v-icon class="toggle-icon" :style="{ color: panelCollapsed ? 'white' : 'var(--v-theme-primary)' }">
                {{ panelCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
              </v-icon>
            </div>
          </div>
        </v-btn>
      </v-card-action>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'NodePanel',
  emits: ['add-node'],
  data() {
    return {
      panelCollapsed: true,
      searchTerm: '',
      openPanels: [0, 1, 2, 3, 4],
      // Node types marked as preview/beta — keep in sync with FlowNode.vue#previewNodeTypes.
      previewNodeTypes: ['advancedRouter', 'email'],
      nodeCategories: [
        {
          name: 'Input/Output',
          icon: 'mdi-import',
          nodes: [
            {
              type: 'messageInput',
              name: 'Message Input',
              description: 'User text input',
              icon: 'mdi-chat',
              iconBg: '#ecfdf5',
              iconColor: '#10b981',
              data: { label: 'Message Input', type: 'messageInput' }
            },
            {
              type: 'webhookTrigger',
              name: 'Webhook Trigger',
              description: 'Run flow via external API call',
              icon: 'mdi-webhook',
              iconBg: '#fdf4ff',
              iconColor: '#a855f7',
              data: { label: 'Webhook Trigger', type: 'webhookTrigger' }
            },
            // {
            //   type: 'api',
            //   name: 'API Call',
            //   description: 'External API request',
            //   icon: 'mdi-api',
            //   iconBg: '#fffbeb',
            //   iconColor: '#f59e0b',
            //   data: { label: 'API Call', type: 'api' }
            // },
            {
              type: 'response',
              name: 'Response',
              description: 'Bot response output',
              icon: 'mdi-message-reply-text',
              iconBg: '#ffeded',
              iconColor: '#ef4444 ',
              data: { label: 'Response', type: 'response' }
            },
            // {
            //   type: 'textOutput',
            //   name: 'Text Output',
            //   description: 'Display text result',
            //   icon: 'mdi-text-box',
            //   iconBg: '#e0f2fe',
            //   iconColor: '#0288d1',
            //   data: { label: 'Text Output', type: 'textOutput' }
            // }
          ]
        },
        {
          name: 'AI',
          icon: 'mdi-brain',
          nodes: [
            {
              type: 'model',
              name: 'AI Model',
              description: 'Language model processor',
              icon: 'mdi-robot',
              iconBg: '#eff6ff',
              iconColor: '#2c6dee',
              data: { label: 'AI Model', type: 'model' }
            },
            // {
            //   type: 'guardrailAI',
            //   name: 'Guard Rail Model',
            //   description: 'Content safety & validation',
            //   icon: 'mdi-shield-check',
            //   iconBg: '#f0fdf4',
            //   iconColor: '#16a34a',
            //   data: { label: 'Guard Rail Model', type: 'guardrailAI' }
            // },
            {
              type: 'intentAI',
              name: 'Intent AI Model',
              description: 'AI-powered intent detection',
              icon: 'mdi-target-variant',
              iconBg: '#f3eeff',
              iconColor: '#8b5cf6',
              data: { label: 'Intent AI Model', type: 'intentAI' }
            },
            {
              type: 'queryRewriter',
              name: 'Query Rewriter',
              description: 'Merge context or summarize history',
              icon: 'mdi-message-text-clock-outline',
              iconBg: '#f3e8ff',
              iconColor: '#7c3aed',
              data: {
                label: 'Query Rewriter',
                type: 'queryRewriter',
                queryRewriterMode: 'continueQuestion',
                queryRewriterProvider: null,
                queryRewriterModel: null,
                queryRewriterApiVersion: null,
                queryRewriterTemperature: 0,
                queryRewriterMaxTokens: 500,
                queryRewriterTopP: 1,
                queryRewriterFrequencyPenalty: null,
                queryRewriterPresencePenalty: null,
                queryRewriterSystemPrompt: '',
                queryRewriterMemoryTurn: 5,
                queryRewriterMemoryDuration: 60,
                queryRewriterStreamResponse: false,
                queryRewriterJsonResponse: false,
              }
            },
          ]
        },
        {
          name: 'Intent',
          icon: 'mdi-target',
          nodes: [
            {
              type: 'intent',
              name: 'Intent Router',
              description: 'Handle specific intent',
              icon: 'mdi-crosshairs-question',
              iconBg: '#f3eeff',
              iconColor: '#8b5cf6',
              data: { label: 'Intent Router', type: 'intent', intentName: 'New Intent' }
            },
            {
              type: 'smartIntent',
              name: 'Smart Intent',
              description: 'AI-enhanced intent handler with individual AI config',
              icon: 'mdi-robot-confused',
              iconBg: '#f0f4ff',
              iconColor: '#4f46e5',
              data: { label: 'Smart Intent', type: 'smartIntent', smartIntentName: 'New Smart Intent' }
            },
            {
              type: 'advancedRouter',
              name: 'Advanced Router',
              description: 'Hierarchical multi-level intent routing',
              icon: 'mdi-source-branch',
              iconBg: '#f3e8ff',
              iconColor: '#7c3aed',
              data: {
                label: 'Advanced Router',
                type: 'advancedRouter',
                advancedRouterName: 'New Advanced Router',
                advancedRouterPrompt: '',
                advancedRouterConfidenceThreshold: 0.5,
                advancedRouterMaxDepth: 2,
                advancedRouterOutputs: [],
                customSchemaEnabled: false,
                schemaFieldMapping: {
                  intentField: 'capability_intent',
                  confidenceField: 'confidence',
                  subIntentsField: 'intents'
                }
              }
            },
            {
              type: 'jsonCondition',
              name: 'JSON Condition',
              description: 'Rule-based JSON routing (no AI cost)',
              icon: 'mdi-code-json',
              iconBg: '#ccfbf1',
              iconColor: '#0d9488',
              data: {
                label: 'JSON Condition',
                type: 'jsonCondition',
                jsonConditionName: 'New JSON Condition',
                matchMode: 'all',
                conditions: [],
                matchLabel: 'Match',
                fallbackLabel: 'Fallback'
              }
            },
            {
              type: 'apiTrigger',
              name: 'API Trigger',
              description: 'Trigger external API action',
              icon: 'mdi-rocket-launch',
              iconBg: '#f0f9ff',
              iconColor: '#0ea5e9',
              data: { label: 'API Trigger', type: 'apiTrigger' }
            },
            {
              type: 'contactAgent',
              name: 'Contact Agent',
              description: 'Transfer to human agent',
              icon: 'mdi-account-supervisor',
              iconBg: '#fef2f2',
              iconColor: '#dc2626',
              data: { label: 'Contact Agent', type: 'contactAgent' }
            }
          ]
        },
        {
          name: 'Knowledge',
          icon: 'mdi-book-open-variant',
          nodes: [
            {
              type: 'documents',
              name: 'Documents',
              description: 'Document management and search',
              icon: 'mdi-file-document-multiple',
              iconBg: '#f0f9ff',
              iconColor: '#0369a1',
              data: { label: 'Documents', type: 'documents' }
            },
            {
              type: 'databaseIntegration',
              name: 'Database Integration',
              description: 'Connect to knowledge database',
              icon: 'mdi-database-search',
              iconBg: '#f0fdf4',
              iconColor: '#16a34a',
              data: { label: 'Database Integration', type: 'databaseIntegration' }
            },
            {
              type: 'apiknowledge',
              name: 'API Knowledge',
              description: 'Fetch knowledge from external API',
              icon: 'mdi-rocket-launch',
              iconBg: '#ffecdb',
              iconColor: '#ff6c37',
              data: { label: 'Knowledge API', type: 'apiknowledge' }
            },
            {
              type: 'products',
              name: 'Products',
              description: 'Product information and catalog',
              icon: 'mdi-package-variant',
              iconBg: '#fef3c7',
              iconColor: '#d97706',
              data: { label: 'Products', type: 'products' }
            },
            {
              type: 'sharepoint',
              name: 'SharePoint',
              description: 'Connect to SharePoint and get files',
              icon: 'mdi-microsoft-sharepoint',
              iconBg: '#e6f7f8',
              iconColor: '#02787c',
              data: { label: 'SharePoint', type: 'sharepoint' }
            }
          ]
        },
        {
          name: 'Logic',
          icon: 'mdi-swap-horizontal',
          nodes: [
            {
              type: 'merge',
              name: 'Merge',
              description: 'Merge parallel AI Model outputs',
              icon: 'mdi-call-merge',
              iconBg: '#e0f2fe',
              iconColor: '#0ea5e9',
              data: { label: 'Merge', type: 'merge' }
            }
          ]
        },
        {
          name: 'Actions',
          icon: 'mdi-lightning-bolt',
          nodes: [
            {
              type: 'email',
              name: 'Email',
              description: 'Send email notifications',
              icon: 'mdi-email-send',
              iconBg: '#fef3c7',
              iconColor: '#f59e0b',
              data: { label: 'Email', type: 'email' }
            }
          ]
        },
        
      ]
    }
  },
  computed: {
    filteredCategories() {
      if (!this.searchTerm) {
        return this.nodeCategories;
      }

      return this.nodeCategories.map(category => ({
        ...category,
        nodes: category.nodes.filter(node =>
          node.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
          node.description.toLowerCase().includes(this.searchTerm.toLowerCase())
        )
      })).filter(category => category.nodes.length > 0);
    },
    quickNodes() {
      // Most commonly used nodes for collapsed state
      return [
        this.nodeCategories[0].nodes[0], // Message Input
        this.nodeCategories[0].nodes[1], // Response
        this.nodeCategories[1].nodes[0], // AI Model
        this.nodeCategories[2].nodes[0], // Intent Handler
        this.nodeCategories[2].nodes[1], // Smart Intent
        this.nodeCategories[1].nodes[1], // Intent AI Model
        this.nodeCategories[4].nodes[0], // Merge
        this.nodeCategories[3].nodes[0], // Documents
      ];
    }
  },
  methods: {
    togglePanel() {
      this.panelCollapsed = !this.panelCollapsed;
    },
    onDragStart(event, node) {
      // Store node data for drop handling
      event.dataTransfer.setData('application/reactflow', JSON.stringify(node));
      event.dataTransfer.effectAllowed = 'move';
    },
    addNode(nodeTemplate) {
      // Generate unique ID
      const nodeId = `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const newNode = {
        id: nodeId,
        type: 'flow-node',
        position: { x: 100, y: 100 }, // Default position, will be updated
        data: { ...nodeTemplate.data }
      };

      this.$emit('add-node', newNode);
    }
  }
}
</script>


<style scoped>
.floating-node-panel {
  position: absolute;
  top: 150px;
  left: 20px;
  z-index: 1000;
  width: 320px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: calc(100vh - 100px);
  height: 85%;
}

.floating-node-panel.collapsed {
  width: 70px;
  height: auto;
  min-height: 400px;
  max-height: calc(100vh - 100px);
}

.panel-card {
  border-radius: 16px !important;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  padding: 16px 20px !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white !important;
  border-radius: 16px 16px 0 0 !important;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: auto;
  flex-shrink: 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 8px;
}

.panel-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.toggle-btn {
  color: rgba(255, 255, 255, 0.9) !important;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.toggle-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.panel-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px !important;
  max-height: calc(100vh - 120px);
}

.search-container {
  margin-bottom: 16px;
  flex-shrink: 0;
}

.categories {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.category-panels {
  background: transparent;
}

.category-header {
  padding: 12px !important;
}

.category-title-content {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.category-icon {
  color: #6b7280;
}

.category-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  flex: 1;
}

.node-count {
  background: #f3f4f6;
  color: #6b7280;
}

.category-content {
  padding: 0 !important;
}

.category-nodes {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 8px 16px;
}

.draggable-node {
  position: relative;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: white;
  cursor: grab;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;
}

.preview-chip-panel {
  position: absolute;
  top: 6px;
  right: 6px;
  padding: 2px 7px;
  background: rgba(139, 92, 246, 0.12);
  color: #6366F1;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  border-radius: 8px;
  border: 1px solid rgba(139, 92, 246, 0.25);
  pointer-events: none;
  user-select: none;
  white-space: nowrap;
  z-index: 2;
}

/* Reserve room for the floating PREVIEW chip on the name row only —
   description stays full-width because the chip sits above it, not beside it. */
.draggable-node.is-preview-node .node-name {
  padding-right: 56px;
}

.draggable-node:hover {
  background: #e8f3ff;
  border-color: #b5cae9;
  transform: translateY(-2px);
}

.draggable-node:active {
  cursor: grabbing;
  transform: translateY(0);
}

.node-preview {
  display: flex;
  align-items: center;
  gap: 12px;
}

.node-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.node-info {
  flex: 1;
  min-width: 0;
}

.node-name {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 2px;
}

.node-description {
  font-size: 11px;
  color: #6b7280;
  line-height: 1.3;
}

/* Collapsed state */
.collapsed-content {
  padding: 16px 8px !important;
  /* แก้ไขใน collapsed state */
  flex: 1;
  overflow-y: auto;
  max-height: calc(100vh - 120px);
}

.quick-nodes {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

.quick-node {
  width: 44px;
  height: 44px;
  border: 1px solid #dddee2;
  border-radius: 12px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.quick-node:hover {
  background: #e3f1ff;
  border-color: #bad6ff;
  transform: translateY(-2px);
}

.quick-node:active {
  cursor: grabbing;
  transform: translateY(0);
}

/* Scrollbar styling */
.panel-content::-webkit-scrollbar,
.collapsed-content::-webkit-scrollbar {
  width: 6px;
}

.panel-content::-webkit-scrollbar-track,
.collapsed-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}

.panel-content::-webkit-scrollbar-thumb,
.collapsed-content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.panel-content::-webkit-scrollbar-thumb:hover,
.collapsed-content::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

/* Responsive */
@media (max-width: 768px) {
  .floating-node-panel {
    width: 280px;
    top: 10px;
    left: 10px;
    max-height: calc(100vh - 20px);
  }

  .floating-node-panel.collapsed {
    width: 60px;
  }

  .panel-content {
    max-height: calc(100vh - 100px);
  }

  .collapsed-content {
    max-height: calc(100vh - 100px);
  }

  .draggable-node {
    padding: 10px;
  }

  .node-name {
    font-size: 12px;
  }

  .node-description {
    font-size: 10px;
  }
}

/* Glass effect enhancement */
@supports (backdrop-filter: blur(10px)) {
  .panel-card {
    background: rgba(255, 255, 255, 0.85) !important;
  }
}

/* Enhanced Toggle Button with Hint Animation */
.collapse-btn {
  position: relative;
  overflow: visible;
  background: transparent !important;
  color: rgb(var(--v-theme-primary)) !important;
  border-radius: 12px !important;
  box-shadow: none !important;
  min-width: 120px;
  height: 36px;
}

.animated-hint-btn:hover {
  transform: translateY(-2px);
  filter: brightness(0.9);
  animation-play-state: paused;
}

.animated-hint-btn.collapsed-hint {
  min-width: 40px;
  width: 40px;
  height: 40px;
  border-radius: 50% !important;
  background: rgb(var(--v-theme-primary)) !important;
  box-shadow: 0 4px 16px rgba(var(--v-theme-primary), 0.4) !important;
  animation: hint-bounce-collapsed 6s infinite;
}

.animated-hint-btn.collapsed-hint:hover {
  filter: brightness(0.9);
  box-shadow: 0 8px 24px rgba(var(--v-theme-primary), 0.6) !important;
  animation-play-state: paused;
}

/* Bounce Animations */
@keyframes hint-bounce {

  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }

  10% {
    transform: translateY(-8px) scale(1.05);
  }

  30% {
    transform: translateY(-4px) scale(1.02);
  }

  40% {
    transform: translateY(-2px) scale(1.01);
  }

  60% {
    transform: translateY(-6px) scale(1.03);
  }

  70% {
    transform: translateY(-2px) scale(1.01);
  }
}

@keyframes hint-bounce-collapsed {

  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0) scale(1);
  }

  10% {
    transform: translateY(-6px) scale(1.1);
  }

  30% {
    transform: translateY(-3px) scale(1.05);
  }

  40% {
    transform: translateY(-1px) scale(1.02);
  }

  60% {
    transform: translateY(-5px) scale(1.08);
  }

  70% {
    transform: translateY(-2px) scale(1.03);
  }
}

.btn-content {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  z-index: 2;
}

.original-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
}

.collapse-text {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.toggle-icon {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Hint Animation Circles */
.hint-circle {
  position: absolute;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  pointer-events: none;
  opacity: 0;
}

.hint-circle-1 {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 60px;
  height: 60px;
  animation: hint-pulse 2s infinite;
  animation-delay: 0s;
}

.hint-circle-2 {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 80px;
  height: 80px;
  animation: hint-pulse 2s infinite;
  animation-delay: 0.3s;
}

.hint-circle-3 {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100px;
  height: 100px;
  animation: hint-pulse 2s infinite;
  animation-delay: 0.6s;
}

/* Collapsed state hint circles */
.collapsed-hint .hint-circle-1 {
  width: 50px;
  height: 50px;
  border-color: rgba(255, 255, 255, 0.8);
}

.collapsed-hint .hint-circle-2 {
  width: 70px;
  height: 70px;
  border-color: rgba(255, 255, 255, 0.6);
}

.collapsed-hint .hint-circle-3 {
  width: 90px;
  height: 90px;
  border-color: rgba(255, 255, 255, 0.4);
}

/* Enhanced Keyframe animations */
@keyframes hint-pulse {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.3);
  }

  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.4);
  }
}

/* Text transition animation */
.collapse-text-enter-active,
.collapse-text-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.collapse-text-enter-from {
  opacity: 0;
  transform: translateX(-10px);
}

.collapse-text-leave-to {
  opacity: 0;
  transform: translateX(10px);
}

/* Hover effects - Stop all animations */
.animated-hint-btn:hover .hint-circle {
  animation-play-state: paused;
  opacity: 0 !important;
}

/* Focus and active states */
.animated-hint-btn:focus .hint-circle,
.animated-hint-btn:active .hint-circle {
  animation-play-state: paused;
  opacity: 0 !important;
}

.animated-hint-btn:focus,
.animated-hint-btn:active {
  animation-play-state: paused;
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .hint-circle-1 {
    width: 50px;
    height: 50px;
  }

  .hint-circle-2 {
    width: 70px;
    height: 70px;
  }

  .hint-circle-3 {
    width: 90px;
    height: 90px;
  }

  .collapsed-hint .hint-circle-1 {
    width: 45px;
    height: 45px;
  }

  .collapsed-hint .hint-circle-2 {
    width: 65px;
    height: 65px;
  }

  .collapsed-hint .hint-circle-3 {
    width: 85px;
    height: 85px;
  }

  /* Smaller bounce for mobile */
  @keyframes hint-bounce {

    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }

    10% {
      transform: translateY(-4px) scale(1.03);
    }

    30% {
      transform: translateY(-2px) scale(1.01);
    }

    60% {
      transform: translateY(-3px) scale(1.02);
    }

    70% {
      transform: translateY(-1px) scale(1.01);
    }
  }
}

/* Accessibility - Respect prefers-reduced-motion */
@media (prefers-reduced-motion: reduce) {
  .animated-hint-btn {
    animation: none;
  }

  .hint-circle {
    animation: none;
    opacity: 0;
  }
}
</style>